import React, { useCallback, useEffect, useRef, useState } from "react";
import CallLeadDetails from "./CallLeadDetails";
import {
  fetchLeadDetails,
  fetchLeadActivities,
  fetchLeadAssignmentHistory,
  fetchRecentLeadOrdersByLead,
  recordLeadAction,
  saveLeadOutcome,
} from "../callWorkspaceApi";
import { fetchLeadConsultations } from "./consultations/consultationsApi";
import { fetchLeadFinance } from "./finance/financeApi";
import { fetchLeadClaims, fetchPackagePolicies } from "./claims/claimsApi";
import { fetchLeadClaimTransactions } from "./transactions/transactionsApi";
import { getApiErrorMessage } from "utils/getApiErrorMessage";
import { formatDate, getToken, OUTCOMES } from "../callWorkspace.utils";
import { sanitizeCards } from "./CallLeadDetails.utils";
import {
  emptyPage,
  emptyResource,
  emptyRecentResource,
  pageFromResponse,
  toLocalInput,
  transactionPageFromResponse,
} from "./CallLeadDetailsContainer.utils";

const CallLeadDetailsContainer = ({ match, history, location }) => {
  const { leadId } = match.params;
  const taskId = new URLSearchParams(location.search).get("taskId");
  const requestKey = String(leadId) + ":" + String(taskId || "profile");
  const activeRequest = useRef(requestKey);
  const [details, setDetails] = useState(null);
  const [form, setForm] = useState({
    outcome: "not_called",
    remarks: "",
    next_follow_up_at: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [medicineOrders, setMedicineOrders] = useState(emptyRecentResource);
  const [labOrders, setLabOrders] = useState(emptyRecentResource);
  const [consultations, setConsultations] = useState(emptyResource);
  const [claims, setClaims] = useState(emptyResource);
  const [claimTransactions, setClaimTransactions] = useState(emptyResource);
  const [finance, setFinance] = useState({
    cards: [],
    bank: null,
    cardsLoading: false,
    bankLoading: false,
    cardsLoaded: false,
    bankLoaded: false,
    cardsError: "",
    bankError: "",
  });
  const [policies, setPolicies] = useState({
    data: [],
    loading: false,
    loaded: false,
    error: "",
  });
  const [activities, setActivities] = useState({
    data: [],
    current_page: 1,
    last_page: 1,
    total: 0,
    loading: false,
    loaded: false,
    error: "",
  });
  const [assignmentHistory, setAssignmentHistory] = useState({
    data: [],
    loading: false,
    loaded: false,
    error: "",
  });

  const current = useCallback((key) => activeRequest.current === key, []);

  const loadPage = useCallback(
    (kind, id, page, force) => {
      const config = {
        consultations: [
          consultations,
          setConsultations,
          fetchLeadConsultations,
          {},
        ],
        claims: [claims, setClaims, fetchLeadClaims, { page_size: 10 }],
        transactions: [
          claimTransactions,
          setClaimTransactions,
          fetchLeadClaimTransactions,
          { page_size: 10 },
        ],
      }[kind];
      if (!config || (!force && (config[0].loaded || config[0].loading)))
        return Promise.resolve();
      const activeKey = activeRequest.current;
      config[1]((state) => ({ ...state, loading: true, error: "" }));
      return config[2](getToken(), id, { ...config[3], page: page || 1 })
        .then((response) => {
          if (!current(activeKey)) return;
          config[1]({
            data:
              kind === "transactions"
                ? transactionPageFromResponse(response)
                : pageFromResponse(response),
            loading: false,
            loaded: true,
            error: "",
          });
        })
        .catch((apiError) => {
          if (current(activeKey))
            config[1]((state) => ({
              ...state,
              loading: false,
              loaded: true,
              error: getApiErrorMessage(apiError),
            }));
        });
    },
    [claimTransactions, claims, consultations, current],
  );

  const loadRecentOrders = useCallback(() => {
    const activeKey = activeRequest.current;
    setMedicineOrders((state) => ({ ...state, loading: true, error: "" }));
    setLabOrders((state) => ({ ...state, loading: true, error: "" }));
    return fetchRecentLeadOrdersByLead(getToken(), leadId)
      .then((response) => {
        if (!current(activeKey)) return;
        const payload =
          response && response.data ? response.data : response || {};
        const period = payload.period || null;
        setMedicineOrders({
          data: payload.medicine_orders || [],
          period,
          loading: false,
          loaded: true,
          error: "",
        });
        setLabOrders({
          data: payload.lab_orders || [],
          period,
          loading: false,
          loaded: true,
          error: "",
        });
      })
      .catch((apiError) => {
        if (!current(activeKey)) return;
        const message = getApiErrorMessage(apiError);
        setMedicineOrders((state) => ({
          ...state,
          loading: false,
          loaded: true,
          error: message,
        }));
        setLabOrders((state) => ({
          ...state,
          loading: false,
          loaded: true,
          error: message,
        }));
      });
  }, [current, leadId]);

  const loadActivities = useCallback(
    (page = 1) => {
      const activeKey = activeRequest.current;
      setActivities((state) => ({ ...state, loading: true, error: "" }));
      return fetchLeadActivities(getToken(), leadId, { page, page_size: 20 })
        .then((response) => {
          if (!current(activeKey)) return;
          const payload =
            response && response.data ? response.data : response || {};
          setActivities({
            data: payload.data || [],
            current_page: payload.current_page || 1,
            last_page: payload.last_page || 1,
            total: payload.total || 0,
            loading: false,
            loaded: true,
            error: "",
          });
        })
        .catch((apiError) => {
          if (current(activeKey))
            setActivities((state) => ({
              ...state,
              loading: false,
              loaded: true,
              error: getApiErrorMessage(apiError),
            }));
        });
    },
    [current, leadId],
  );

  const loadAssignmentHistory = useCallback(() => {
    const activeKey = activeRequest.current;
    setAssignmentHistory((state) => ({ ...state, loading: true, error: "" }));
    return fetchLeadAssignmentHistory(getToken(), leadId)
      .then((response) => {
        if (!current(activeKey)) return;
        const payload = response && response.data ? response.data : {};
        setAssignmentHistory({
          data: payload.history || [],
          loading: false,
          loaded: true,
          error: "",
        });
      })
      .catch((apiError) => {
        if (current(activeKey)) {
          setAssignmentHistory({
            data: [],
            loading: false,
            loaded: true,
            error: getApiErrorMessage(apiError),
          });
        }
      });
  }, [current, leadId]);

  const loadFinance = useCallback(
    (id) => {
      const activeKey = activeRequest.current;
      if (
        (finance.cardsLoaded && finance.bankLoaded) ||
        finance.cardsLoading ||
        finance.bankLoading
      )
        return;

      setFinance((state) => ({
        ...state,
        cardsLoading: true,
        bankLoading: true,
        cardsError: "",
        bankError: "",
      }));
      fetchLeadFinance(getToken(), id)
        .then((response) => {
          if (current(activeKey)) {
            const payload = response.data || {};
            setFinance((state) => ({
              ...state,
              cards: sanitizeCards(payload.cards),
              bank: payload.lead_detail || null,
              cardsLoading: false,
              bankLoading: false,
              cardsLoaded: true,
              bankLoaded: true,
            }));
          }
        })
        .catch((apiError) => {
          if (current(activeKey)) {
            const error = getApiErrorMessage(apiError);
            setFinance((state) => ({
              ...state,
              cardsLoading: false,
              bankLoading: false,
              cardsLoaded: true,
              bankLoaded: true,
              cardsError: error,
              bankError: error,
            }));
          }
        });
    },
    [current, finance.bankLoaded, finance.bankLoading, finance.cardsLoaded, finance.cardsLoading],
  );

  const loadPolicies = useCallback(
    (packages) => {
      if (policies.loaded || policies.loading) return;
      const activeKey = activeRequest.current;
      const ids = Array.from(
        new Set(
          (packages || []).map((item) => item.package_id).filter(Boolean),
        ),
      );
      if (!ids.length)
        return setPolicies({
          data: [],
          loading: false,
          loaded: true,
          error: "",
        });
      setPolicies((state) => ({ ...state, loading: true, error: "" }));
      Promise.all(
        ids.map((packageId) =>
          fetchPackagePolicies(getToken(), packageId).then((response) => ({
            packageId,
            data: Array.isArray(response.data) ? response.data : [],
          })),
        ),
      )
        .then((data) => {
          if (current(activeKey))
            setPolicies({ data, loading: false, loaded: true, error: "" });
        })
        .catch((apiError) => {
          if (current(activeKey))
            setPolicies({
              data: [],
              loading: false,
              loaded: true,
              error: getApiErrorMessage(apiError),
            });
        });
    },
    [current, policies.loaded, policies.loading],
  );

  const loadDetails = useCallback(
    (showLoader) => {
      const activeKey = activeRequest.current;
      if (showLoader) setLoading(true);
      setError("");
      return fetchLeadDetails(getToken(), leadId, taskId)
        .then((response) => {
          if (!current(activeKey)) return null;
          const data = response.data || {};
          const task = data.task || {};
          setDetails(data);
          if (task)
            setForm({
              outcome: task.outcome || "not_called",
              remarks: task.remarks || "",
              next_follow_up_at: toLocalInput(task.next_follow_up_at),
            });
          return data;
        })
        .catch((apiError) => {
          if (current(activeKey)) setError(getApiErrorMessage(apiError));
          return null;
        })
        .finally(() => {
          if (current(activeKey) && showLoader) setLoading(false);
        });
    },
    [current, leadId, taskId],
  );

  useEffect(() => {
    document.title = "Lead Details | Call Workspace";
    activeRequest.current = requestKey;
    setDetails(null);
    setMedicineOrders(emptyRecentResource());
    setLabOrders(emptyRecentResource());
    setConsultations(emptyResource());
    setClaims(emptyResource());
    setClaimTransactions(emptyResource());
    setFinance({
      cards: [],
      bank: null,
      cardsLoading: false,
      bankLoading: false,
      cardsLoaded: false,
      bankLoaded: false,
      cardsError: "",
      bankError: "",
    });
    setPolicies({ data: [], loading: false, loaded: false, error: "" });
    setActivities({
      data: [],
      current_page: 1,
      last_page: 1,
      total: 0,
      loading: false,
      loaded: false,
      error: "",
    });
    setAssignmentHistory({
      data: [],
      loading: false,
      loaded: false,
      error: "",
    });
    loadDetails(true);
    loadRecentOrders();
    loadActivities(1);
    loadAssignmentHistory();
  }, [leadId, taskId]);

  const lead = details && details.lead;
  const handleTabSelect = (tab) => {
    if (!lead) return;
    if (tab === "consultations") loadPage(tab, lead.id, 1, false);
    if (tab === "finance") loadFinance(lead.id);
    if (tab === "claims") {
      loadPage("claims", lead.id, 1, false);
      loadPage("transactions", lead.id, 1, false);
      loadPolicies(lead.lead_catalogue_package || []);
    }
  };

  const saveOutcome = (event) => {
    event.preventDefault();
    if (!taskId) return Promise.resolve(false);
    setSaving(true);
    setError("");
    return saveLeadOutcome(getToken(), taskId, {
      ...form,
      next_follow_up_at:
        form.outcome === "follow_up" ? form.next_follow_up_at : null,
    })
      .then((response) => {
        setSuccess(response.message || "Lead outcome updated.");
        return Promise.all([loadDetails(false), loadActivities(1)]).then(
          () => true,
        );
      })
      .catch((apiError) => {
        setError(getApiErrorMessage(apiError));
        return false;
      })
      .finally(() => setSaving(false));
  };

  const performAction = (action) => {
    if (!taskId || !lead) return;
    setActing(action);
    setError("");
    recordLeadAction(getToken(), taskId, action)
      .then(() => {
        if (action === "create_order")
          return history.push("/admin/add-order/" + lead.id);
        if (action === "call" && lead.mobile_no)
          window.location.href = "tel:" + lead.mobile_no;
        if (action === "whatsapp" && lead.mobile_no)
          window.open(
            "https://wa.me/" + String(lead.mobile_no).replace(/\D/g, ""),
            "_blank",
            "noopener,noreferrer",
          );
        setSuccess(
          action === "call" ? "Call recorded." : "WhatsApp action recorded.",
        );
        loadDetails(false);
        loadActivities(1);
      })
      .catch((apiError) => setError(getApiErrorMessage(apiError)))
      .finally(() => setActing(""));
  };

  const resetOutcomeForm = () => {
    const task = details && details.task;
    if (task)
      setForm({
        outcome: task.outcome || "not_called",
        remarks: task.remarks || "",
        next_follow_up_at: toLocalInput(task.next_follow_up_at),
      });
  };

  return (
    <CallLeadDetails
      details={details ? { ...details, activities } : details}
      form={form}
      outcomes={OUTCOMES}
      loading={loading}
      saving={saving}
      acting={acting}
      error={error}
      success={success}
      medicineOrders={medicineOrders}
      labOrders={labOrders}
      consultations={consultations}
      finance={finance}
      claims={claims}
      claimTransactions={claimTransactions}
      policies={policies}
      formatDate={formatDate}
      assignmentHistory={assignmentHistory}
      readOnly={!taskId || !!(details && details.read_only)}
      onBack={() => history.push("/admin/call-workspace")}
      onFormChange={(event) => {
        const { name, value } = event.target;
        setForm((state) => ({ ...state, [name]: value }));
      }}
      onSaveOutcome={saveOutcome}
      onResetOutcome={resetOutcomeForm}
      onAction={performAction}
      onTabSelect={handleTabSelect}
      onConsultationsPage={(page) =>
        loadPage("consultations", lead && lead.id, page, true)
      }
      onClaimsPage={(page) => loadPage("claims", lead && lead.id, page, true)}
      onClaimTransactionsPage={(page) =>
        loadPage("transactions", lead && lead.id, page, true)
      }
      onActivitiesPage={loadActivities}
      onRetryAssignmentHistory={loadAssignmentHistory}
      onDismissMessage={() => {
        setError("");
        setSuccess("");
      }}
    />
  );
};

export default CallLeadDetailsContainer;
